import { memo, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useTrackViewedAsset } from '@air/analytics';
import { type PublicClip } from '@air/api';
import { PlayerContextProvider } from '@air/component-player';
import { useAirModal } from '@air/provider-modal';
import { useShortIdContext } from '@air/provider-short-id';

import { usePublicPageViewNotification } from '~/components/AirNotifications/hooks/usePublicPageViewNotification';
import { PublicClipAssetModalDesktopLayout } from '~/components/AssetModalV2/PublicClipAssetModalDesktopLayout';
import { PublicClipAssetModalMobileLayout } from '~/components/PublicAssetModal/components/PublicClipAssetModalMobileLayout';
import { usePublicAssetClip } from '~/components/PublicAssetModal/hooks/usePublicAssetClip';
import { PublicAssetAnonymousSignUpModal } from '~/components/PublicLinkAnonymousSignUpModal/PublicAssetAnonymousSignUpModal';
import { usePublicSSOAssetCallback } from '~/components/PublicSSOCallback/usePublicSSOAssetCallback';
import { AssetModal } from '~/components/v2/components/asset-modal/AssetModal';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';
import { useOnMissingUserSession } from '~/hooks/useOnMissingUserSession';
import { useAccountContext } from '~/providers/AccountProvider';
import { usePublicAssetModalNavigation } from '~/providers/PublicAssetModalNavigationProvider';
import {
  centralizedClipAssetIdSelector,
  centralizedClipIdSelector,
  centralizedClipIsDemoSelector,
} from '~/store/centralizedClip/selectors';

export type PublicAssetProps = {
  initialClip: PublicClip;
  requireAccount: boolean;
};

export const PublicAsset = memo(({ initialClip, requireAccount }: PublicAssetProps) => {
  const assetId = useSelector(centralizedClipAssetIdSelector);
  const urlClipId = useSelector(centralizedClipIdSelector);
  const { shortId } = useShortIdContext();
  const isDemo = useSelector(centralizedClipIsDemoSelector);
  const { trackViewedAsset } = useTrackViewedAsset();
  const clipId = urlClipId || initialClip.id;
  // The clip page's asset-modal navigation contract (PublicUrlAssetModalNavigationProvider) owns the
  // close; it rides props into the desktop layout (the clip page's mobile layout has no close affordance).
  const { onCloseAssetModal } = usePublicAssetModalNavigation();

  const { data: account } = useAccountContext();

  usePublicAssetClip({ clipId, initialData: initialClip });

  usePublicSSOAssetCallback();

  const [showAnonymousSignupModal] = useAirModal(PublicAssetAnonymousSignUpModal);

  const { sendPublicPageViewEvent } = usePublicPageViewNotification();

  useOnMissingUserSession({
    onMissingSession: () => showAnonymousSignupModal({}),
    enabled: requireAccount,
  });

  useEffect(() => {
    if (!requireAccount || (requireAccount && !!account)) {
      sendPublicPageViewEvent(shortId);
    }
  }, [account, requireAccount, sendPublicPageViewEvent, shortId]);

  useEffect(() => {
    if (assetId) {
      trackViewedAsset({ assetId, isDemo, clip_id: clipId });
    }
  }, [assetId, clipId, isDemo, trackViewedAsset]);

  const desktop = useMemo(
    () => <PublicClipAssetModalDesktopLayout onCloseAssetModal={onCloseAssetModal} />,
    [onCloseAssetModal],
  );
  const mobile = useMemo(() => <PublicClipAssetModalMobileLayout />, []);

  return (
    <PlayerContextProvider id={clipId} storageKey={ZustandStorageKeys.AssetModalPlayer}>
      {/* Public share-link modals read and write discussions through the shortId'd public API. */}
      <AssetModal desktop={desktop} mobile={mobile} storeId={clipId} />
    </PlayerContextProvider>
  );
});

PublicAsset.displayName = 'PublicAsset';
